import React from 'react';
import { motion } from 'framer-motion';

function ServicesPage() {
  const mainServices = [
    {
      title: 'Wealth Management',
      description: 'We are trusted Wealth Managers that focus on delivering investment solutions for our diverse range of clients based on our broad and accurate insight of financial markets.',
      image: '/images/traders-profit-354772.jpg',
      features: ['Portfolio Management', 'Asset Allocation', 'Risk Assessment', 'Performance Monitoring']
    },
    {
      title: 'Financial Planning',
      description: 'Blockfirst Capital Financial Planning offers expert advice and assistance on reaching your financial goals and objectives. Get insight from our financial experts.',
      image: '/images/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg',
      features: ['Retirement Planning', 'Tax Optimization', 'Estate Planning', 'Goal Setting']
    },
    {
      title: 'Investment Advisory',
      description: 'Whatever your financial needs are, at Blockfirst Capital we help you figure out the right type of investments to help generate enough income and plan your finances efficiently.',
      image: '/images/two-stock-exchange-traders-discussing-dynamic-investment-graph-sellable_31965-313820.jpg_yeupoj.jpg',
      features: ['Market Analysis', 'Investment Research', 'Strategic Planning', 'Performance Tracking']
    }
  ];

  const specializedServices = [
    {
      icon: '💼',
      title: 'Institutional Services',
      description: 'Comprehensive solutions for sovereign institutions, pension funds, and large-scale investors.'
    },
    {
      icon: '🌍',
      title: 'Global Market Access',
      description: 'Direct access to 35+ global markets with local expertise and regulatory compliance.'
    },
    {
      icon: '🔒',
      title: 'Risk Management',
      description: 'Advanced risk frameworks to protect and grow your wealth across market cycles.'
    },
    {
      icon: '📊',
      title: 'Performance Analytics',
      description: 'Real-time portfolio analytics and performance reporting with actionable insights.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img 
          src="/images/real-people/business-meeting-office_1268-21537.png"
          alt="Financial Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-[-0.02em] mb-6">
              <span className="block font-light">Our</span>
              <span className="block font-medium text-[#fbbf24]">Services</span>
            </h1>
            <p className="text-white/90 text-xl sm:text-2xl font-light leading-relaxed max-w-3xl mx-auto">
              Comprehensive wealth management solutions tailored to your unique needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4">Core Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional wealth management services designed for discerning investors
            </p>
          </motion.div>

          <div className="space-y-20">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex flex-col lg:flex-row items-center gap-12 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <h3 className="text-3xl font-light text-gray-900 mb-6">{service.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">{service.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={feature} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-[#0056d7] rounded-full"></div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <motion.a
                    href="/contact"
                    className="inline-block mt-8 px-8 py-4 rounded-full bg-gradient-to-r from-[#0056d7] to-[#003a9e] hover:from-[#003a9e] hover:to-[#002a73] text-white font-medium transition-all duration-500 shadow-lg hover:shadow-blue-500/25"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Learn More
                  </motion.a>
                </div>
                
                <div className="flex-1">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4">Specialized Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced capabilities for sophisticated investment needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {specializedServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach to achieving your financial objectives
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We begin by understanding your financial goals, risk tolerance, and investment preferences.'
              },
              {
                step: '02',
                title: 'Analysis',
                description: 'Our experts analyze your current financial position and identify opportunities for optimization.'
              },
              {
                step: '03',
                title: 'Strategy',
                description: 'We develop a customized investment strategy tailored to your specific needs and objectives.'
              },
              {
                step: '04',
                title: 'Implementation',
                description: 'We execute your strategy with precision and provide ongoing monitoring and adjustments.'
              }
            ].map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#0056d7] text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[#0056d7] to-[#003a9e]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-light text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Schedule a consultation with our wealth management experts
            </p>
            <motion.a
              href="/contact"
              className="inline-block px-12 py-6 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-medium text-xl transition-all duration-500 shadow-2xl hover:shadow-amber-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Consultation
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ServicesPage;
